import { useParams, Navigate } from "react-router-dom";
import { Check, X, Euro, Building2, Home, Heart, Users, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { getCity } from "@/data/cities";

export default function CityPage() {
  const { slug } = useParams();
  const c = getCity(slug);
  if (!c) return <Navigate to="/404" replace />;

  const facts = [
    { icon: Euro, label: "Cost of living", value: c.costOfLiving },
    { icon: Home, label: "Housing", value: c.housing },
    { icon: Building2, label: "Business", value: c.business },
    { icon: Heart, label: "Lifestyle", value: c.lifestyle },
    { icon: Users, label: "Indian community", value: c.community },
    { icon: Sparkles, label: "Best for", value: c.bestFor },
  ];

  return (
    <Layout ctaProps={{ interest: "Relocation & Settlement", title: `Make ${c.name} your home`, text: c.whoFor }}>
      <Seo title={`Living in ${c.name}, Portugal — City Guide | Effizent Europe`} description={`${c.name}: ${c.overview}`} />

      {/* Hero image */}
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <img src={c.image} alt={`${c.name}, Portugal`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/40 to-navy-deep/60" />
        <div className="relative h-full flex items-end">
          <div className="max-w-6xl mx-auto px-6 md:px-8 pb-16 w-full">
            <p className="accent-eyebrow text-gold">Portugal · City Guide</p>
            <h1 className="font-playfair text-5xl md:text-7xl text-warm mt-4">{c.name}</h1>
            <p className="text-warm/80 text-xl mt-3">{c.tagline}</p>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-20">
        <Reveal>
          <p className="text-navy/70 text-xl leading-relaxed max-w-3xl">{c.overview}</p>
        </Reveal>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {facts.map((f, i) => (
            <Reveal key={f.label} delay={i * 0.05}>
              <div className="rounded-2xl bg-white border border-navy/10 p-6">
                <f.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <p className="accent-eyebrow text-navy/40 mt-4" style={{ fontSize: "0.6rem" }}>{f.label}</p>
                <p className="text-navy font-500 mt-1.5">{f.value}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="rounded-3xl bg-olive/5 border border-olive/20 p-8 h-full">
              <p className="font-playfair text-2xl text-navy mb-5">The upside</p>
              <ul className="space-y-3">
                {c.prosCons.pros.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-navy/75"><Check className="h-5 w-5 text-olive shrink-0 mt-0.5" /> {p}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-terracotta/5 border border-terracotta/20 p-8 h-full">
              <p className="font-playfair text-2xl text-navy mb-5">Consider</p>
              <ul className="space-y-3">
                {c.prosCons.cons.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-navy/75"><X className="h-5 w-5 text-terracotta shrink-0 mt-0.5" /> {p}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="mt-14 rounded-3xl bg-navy text-warm p-10">
            <p className="accent-eyebrow text-gold">Who should choose {c.name}?</p>
            <p className="font-playfair text-2xl md:text-3xl mt-4 leading-snug">{c.whoFor}</p>
          </div>
        </Reveal>
      </div>
    </Layout>
  );
}
